import { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, X, Package } from 'lucide-react';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatPrice(n) {
  return `€ ${Number(n).toFixed(2).replace('.', ',')}`;
}

function parseItems(items) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'string') {
    try {
      const p = JSON.parse(items);
      if (Array.isArray(p)) return p;
      if (typeof p === 'string') { const p2 = JSON.parse(p); return Array.isArray(p2) ? p2 : []; }
    } catch { /* fall */ }
  }
  return [];
}

const PRINT_CSS = `
@media print {
  @page { margin: 4mm; size: 100mm 150mm; }
  body * { visibility: hidden; }
  #__order_label_print__, #__order_label_print__ * { visibility: visible !important; }
  #__order_label_print__ {
    display: block !important; position: fixed !important;
    top: 0 !important; left: 0 !important;
    width: 100% !important; background: white !important; z-index: 99999 !important;
  }
}
#__order_label_print__ { display: none; }
`;

function LabelContent({ order, orderNum, labelUrl }) {
  const items    = parseItems(order.items);
  const hasPrice = items.some(i => Number(i.price) > 0);
  const total    = Number(order.total);

  const s = {
    wrap:      { padding: '16px 18px', background: 'white', fontFamily: 'Arial, sans-serif', fontSize: '20px', maxWidth: '460px', boxSizing: 'border-box' },
    header:    { borderBottom: '2px solid #111', paddingBottom: '12px', marginBottom: '12px' },
    company:   { fontWeight: 'bold', fontSize: '24px', textTransform: 'uppercase', letterSpacing: '1.2px' },
    orderRef:  { fontSize: '18px', color: '#555', marginTop: '4px' },
    section:   { borderBottom: '1px solid #ccc', paddingBottom: '10px', marginBottom: '10px' },
    sectionLbl:{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', color: '#888', marginBottom: '5px', letterSpacing: '1.5px' },
    name:      { fontWeight: 'bold', fontSize: '24px', marginBottom: '4px' },
    line:      { fontSize: '19px', marginBottom: '2px', color: '#333' },
    itemRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '19px', marginBottom: '4px' },
    itemName:  { flex: 1, paddingRight: '12px' },
    itemQty:   { fontWeight: 'bold', flexShrink: 0 },
    itemPrice: { flexShrink: 0, marginLeft: '12px' },
    divider:   { borderTop: '1px dashed #aaa', marginTop: '6px', paddingTop: '6px' },
    totalRow:  { display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '22px' },
    qrRow:     { display: 'flex', alignItems: 'center', gap: '14px', paddingTop: '6px' },
    qrLabel:   { fontSize: '16px' },
    qrBold:    { fontWeight: 'bold', fontSize: '16px', textTransform: 'uppercase', letterSpacing: '1px' },
    orderId:   { fontSize: '15px', color: '#999', marginTop: '4px', wordBreak: 'break-all' },
  };

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.company}>Le Bontà del Vesuvio</div>
        <div style={s.orderRef}>Ordine #{orderNum} · {formatDate(order.created_at)}</div>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '4px' }}>Stato: {(order.status || 'pending').replace('_', ' ')}</div>
      </div>

      <div style={s.section}>
        <div style={s.sectionLbl}>Destinatario</div>
        <div style={s.name}>{order.customer_name}</div>
        {order.customer_phone   && <div style={s.line}>{order.customer_phone}</div>}
        {order.customer_email   && <div style={s.line}>{order.customer_email}</div>}
        {order.customer_address && <div style={{ ...s.line, marginTop: '3px' }}>{order.customer_address}</div>}
      </div>

      <div style={s.section}>
        <div style={s.sectionLbl}>Articoli ordinati</div>
        {items.length === 0 ? (
          <div style={{ ...s.line, fontStyle: 'italic', color: '#aaa' }}>Nessun articolo</div>
        ) : (
          <>
            {items.map((item, i) => {
              const price = Number(item.price);
              const qty   = Number(item.quantity) || 1;
              return (
                <div key={i} style={s.itemRow}>
                  <span style={s.itemName}>
                    {item.product_name}
                    {(item.codart || item.ean) && (
                      <span style={{ display: 'block', fontSize: '14px', color: '#666' }}>
                        {item.codart ? `Cod. ${item.codart}` : ''}{item.codart && item.ean ? ' · ' : ''}{item.ean ? `EAN ${item.ean}` : ''}
                      </span>
                    )}
                  </span>
                  <span style={s.itemQty}>×{qty}</span>
                  {hasPrice && <span style={s.itemPrice}>{price > 0 ? formatPrice(price * qty) : '—'}</span>}
                </div>
              );
            })}
            {total > 0 && (
              <div style={s.divider}>
                <div style={s.totalRow}><span>TOTALE</span><span>{formatPrice(total)}</span></div>
              </div>
            )}
          </>
        )}
      </div>

      {order.notes && (
        <div style={s.section}>
          <div style={s.sectionLbl}>Note cliente</div>
          <div style={{ ...s.line, fontStyle: 'italic' }}>{order.notes}</div>
        </div>
      )}

      <div style={s.qrRow}>
        <QRCodeSVG value={labelUrl} size={144} level="M" />
        <div>
          <div style={s.qrBold}>Scansiona per</div>
          <div style={s.qrLabel}>gestire l'ordine</div>
          <div style={s.qrLabel}>stato e dettaglio</div>
          <div style={s.orderId}>#{orderNum}</div>
        </div>
      </div>

      <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '10px', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Gestione ordine</div>
        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2d5a27', wordBreak: 'break-all' }}>
          {labelUrl}
        </div>
      </div>
    </div>
  );
}

export default function OrderLabelModal({ order, onClose, showWhatsApp = false }) {
  const labelRef   = useRef(null);
  const [busy, setBusy] = useState(false);

  const orderNum = order.id.slice(-8).toUpperCase();
  const labelUrl = `${window.location.origin}/admin/ordini/${order.id}`;

  // "Invia a stampante": genera PNG → Web Share → fallback window.print()
  const handleSend = async () => {
    setBusy(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(labelRef.current, {
        scale: 3, backgroundColor: '#ffffff', useCORS: true, logging: false,
      });

      canvas.toBlob(async (blob) => {
        const file = new File([blob], `etichetta-${orderNum}.png`, { type: 'image/png' });

        // Prova Web Share API con file (mobile)
        if (navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({
              title: `Ordine #${orderNum} — Le Bontà del Vesuvio`,
              files: [file],
            });
            setBusy(false);
            return;
          } catch { /* annullato dall'utente */ }
        }

        // Fallback: stampa browser
        const old = document.getElementById('__label_print_css__');
        if (old) old.remove();
        const style = document.createElement('style');
        style.id = '__label_print_css__';
        style.textContent = PRINT_CSS;
        document.head.appendChild(style);
        window.print();
        setTimeout(() => document.getElementById('__label_print_css__')?.remove(), 1500);
        setBusy(false);
      }, 'image/png');
    } catch {
      // Ultimo fallback: stampa browser diretta
      const old = document.getElementById('__label_print_css__');
      if (old) old.remove();
      const style = document.createElement('style');
      style.id = '__label_print_css__';
      style.textContent = PRINT_CSS;
      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById('__label_print_css__')?.remove(), 1500);
      setBusy(false);
    }
  };

  // WhatsApp
  const whatsappUrl = (() => {
    if (!showWhatsApp) return null;
    const phone = order.customer_phone.replace(/\D/g, '');
    const items = parseItems(order.items);
    const lines = items.map(i => {
      const price = Number(i.price), qty = Number(i.quantity) || 1;
      return `• ${i.product_name} ×${qty}${price > 0 ? ` — ${formatPrice(price * qty)}` : ''}`;
    }).join('\n');
    const total = Number(order.total);
    const msg = [
      `✅ *Ordine Confermato — Le Bontà del Vesuvio*`, ``,
      `Ciao ${order.customer_name}! 🌿`, ``,
      `Il tuo ordine è stato confermato e stiamo iniziando la preparazione!`, ``,
      `📦 *Dettaglio ordine:*`, lines || '(articoli in aggiornamento)',
      total > 0 ? `` : null, total > 0 ? `💰 *Totale: ${formatPrice(total)}*` : null,
      ``, `Ti contatteremo appena pronto.`, ``,
      `Grazie per aver scelto Le Bontà del Vesuvio! 🌋🍅`,
    ].filter(l => l !== null).join('\n');
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  })();

  return (
    <>
      <div id="__order_label_print__">
        <LabelContent order={order} orderNum={orderNum} labelUrl={labelUrl} />
      </div>

      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] flex flex-col">

          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-bold text-gray-900 text-sm">Anteprima Etichetta</h3>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-4 space-y-3">

            {/* WhatsApp */}
            {showWhatsApp && whatsappUrl && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <p className="font-body text-xs font-semibold text-green-800 mb-1">Notifica cliente</p>
                <a
                  href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-body font-semibold text-xs transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.115.557 4.102 1.528 5.822L0 24l6.335-1.525A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.01-1.375l-.358-.213-3.75.983.999-3.641-.234-.374A9.818 9.818 0 1112 21.818z" />
                  </svg>
                  Invia conferma su WhatsApp
                </a>
              </div>
            )}

            {/* Label preview */}
            <div>
              <p className="font-body text-xs text-gray-400 uppercase tracking-wide mb-2">Anteprima etichetta</p>
              <div className="border-2 border-gray-700 rounded overflow-hidden" ref={labelRef}>
                <LabelContent order={order} orderNum={orderNum} labelUrl={labelUrl} />
              </div>
            </div>

            {/* Shop link */}
            <a
              href="https://lebontadelvesuvio.gengioia.it/negozio"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full border border-primary/30 text-primary hover:bg-primary/5 py-2 rounded-xl font-body text-xs font-medium transition-colors"
            >
              🛒 lebontadelvesuvio.gengioia.it/negozio
            </a>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 flex gap-2 flex-shrink-0">
            <button
              onClick={handleSend}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 bg-primary-dark hover:bg-primary text-white py-3 rounded-xl font-body font-semibold text-sm transition-colors disabled:opacity-60"
            >
              <Printer className="w-4 h-4" />
              {busy ? 'Preparazione...' : 'Invia a stampante'}
            </button>
            <button
              onClick={onClose}
              className="px-4 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-body text-sm"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
